import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, X, Save } from 'lucide-react';
import { useAdmin } from '../../contexts/AdminContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CampaignForm = () => {
  const { id } = useParams();
  const isEdit = id !== 'new';
  const { getAuthHeaders, isAuthenticated } = useAdmin();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    brand: '',
    category: '',
    year: new Date().getFullYear().toString(),
    thumbnail: '',
    heroImage: '',
    videoLink: '',
    shortDescription: '',
    fullDescription: '',
    challenge: '',
    solution: '',
    published: true,  // New campaigns default to Published; uncheck to save as draft
    results: [
      { metric: '', value: '' },
      { metric: '', value: '' },
      { metric: '', value: '' }
    ],
    images: [],
    translations: {
      fr: {
        shortDescription: '',
        fullDescription: '',
        challenge: '',
        solution: ''
      }
    }
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    
    if (isEdit) {
      fetchCampaign();
    }
  }, [isEdit, id, isAuthenticated]);

  const fetchCampaign = async () => {
    try {
      const response = await fetch(`${API_URL}/api/campaigns/${id}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      // Coerce nullable string fields to '' so controlled inputs don't warn
      setFormData({
        ...data,
        videoLink: data.videoLink ?? '',
        published: data.published !== false,
      });
    } catch (error) {
      console.error('Error fetching campaign:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTranslationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        fr: {
          ...prev.translations.fr,
          [field]: value
        }
      }
    }));
  };

  const handleResultChange = (index, field, value) => {
    const newResults = [...formData.results];
    newResults[index] = { ...newResults[index], [field]: value };
    setFormData(prev => ({ ...prev, results: newResults }));
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/campaigns/upload-image`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formDataUpload
      });

      const data = await response.json();
      setFormData(prev => ({
        ...prev,
        [field]: data.url
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/campaigns/upload-image`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formDataUpload
      });

      const data = await response.json();
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, data.url]
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = isEdit 
        ? `${API_URL}/api/campaigns/${id}`
        : `${API_URL}/api/campaigns/`;
      
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        navigate('/admin/dashboard');
      } else {
        alert('Failed to save campaign');
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('Error saving campaign');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-2 text-slate-600 hover:text-teal-600 transition-colors mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold text-slate-900">
            {isEdit ? 'Edit Campaign' : 'Add New Campaign'}
          </h1>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Brand</label>
                <input
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Year</label>
                <input
                  type="text"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Video Link (Optional)</label>
                <input
                  type="url"
                  name="videoLink"
                  value={formData.videoLink}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    name="published"
                    data-testid="campaign-form-published-checkbox"
                    checked={!!formData.published}
                    onChange={(e) => setFormData(prev => ({ ...prev, published: e.target.checked }))}
                    className="mt-1 w-5 h-5 accent-teal-600"
                  />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {formData.published ? 'Published — live on your public site' : 'Save as Draft — hidden from the public site'}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      Keep this ticked to make the campaign visible on your homepage. Untick it to save privately as a draft (you can publish it later from the dashboard with the eye icon).
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Images</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Thumbnail</label>
                {formData.thumbnail && (
                  <img src={formData.thumbnail} alt="Thumbnail" className="w-full h-32 object-cover rounded-lg mb-2" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'thumbnail')}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                  disabled={uploading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Hero Image</label>
                {formData.heroImage && (
                  <img src={formData.heroImage} alt="Hero" className="w-full h-32 object-cover rounded-lg mb-2" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'heroImage')}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                  disabled={uploading}
                />
              </div>
            </div>
          </div>

          {/* Gallery Images */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Gallery Images</h2>
            <p className="text-sm text-slate-600 mb-6">Upload images for the campaign gallery (shown at bottom of campaign page)</p>
            
            {/* Existing Gallery Images */}
            {formData.images.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {formData.images.map((image, index) => (
                  <div key={index} className="relative group">
                    <img 
                      src={image} 
                      alt={`Gallery ${index + 1}`} 
                      className="w-full h-32 object-cover rounded-lg border-2 border-slate-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(index)}
                      className="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Upload New Gallery Image */}
            <div>
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-teal-500 transition-colors bg-slate-50 hover:bg-teal-50">
                <div className="flex flex-col items-center justify-center py-4">
                  <Upload className="w-8 h-8 text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600">
                    {uploading ? 'Uploading...' : 'Click to upload gallery image'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">PNG, JPG or WEBP</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleGalleryImageUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
          </div>

          {/* English Content */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-6">English Content</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Short Description</label>
                <textarea
                  name="shortDescription"
                  value={formData.shortDescription}
                  onChange={handleChange}
                  rows="2"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Description</label>
                <textarea
                  name="fullDescription"
                  value={formData.fullDescription}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Challenge</label>
                <textarea
                  name="challenge"
                  value={formData.challenge}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Solution</label>
                <textarea
                  name="solution"
                  value={formData.solution}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                  required
                />
              </div>
            </div>
          </div>

          {/* French Translations */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-6">French Translation</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Short Description (FR)</label>
                <textarea
                  value={formData.translations.fr.shortDescription}
                  onChange={(e) => handleTranslationChange('shortDescription', e.target.value)}
                  rows="2"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Description (FR)</label>
                <textarea
                  value={formData.translations.fr.fullDescription}
                  onChange={(e) => handleTranslationChange('fullDescription', e.target.value)}
                  rows="4"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Challenge (FR)</label>
                <textarea
                  value={formData.translations.fr.challenge}
                  onChange={(e) => handleTranslationChange('challenge', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Solution (FR)</label>
                <textarea
                  value={formData.translations.fr.solution}
                  onChange={(e) => handleTranslationChange('solution', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Results (3 metrics)</h2>
            <div className="space-y-4">
              {formData.results.map((result, index) => (
                <div key={index} className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Metric (e.g., Revenue)"
                    value={result.metric}
                    onChange={(e) => handleResultChange(index, 'metric', e.target.value)}
                    className="px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Value (e.g., $6M)"
                    value={result.value}
                    onChange={(e) => handleResultChange(index, 'value', e.target.value)}
                    className="px-4 py-2 border border-slate-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none"
                    required
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              className="px-6 py-3 bg-slate-100 text-slate-700 rounded-lg font-semibold hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploading}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-slate-300"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {isEdit ? 'Update Campaign' : 'Create Campaign'}
                </>
              )}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CampaignForm;
